/**
 * Decides whether the post-auth prequalification wizard should be shown to the
 * current user during the post-login root bootstrap (ENG-4175).
 */

import { FIRST_LOGIN_CLAIM, useAuth0 } from '#~/auth/index.js';
import { useConfig } from '#~/config/hooks.js';
import { useDepot } from '#~/depot/hooks.js';
import { isElectronApp } from '#~/electron/utils.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { hasPrequalPrompted } from './prequal-first-login.js';

export type PrequalGateStatus = 'loading' | 'show' | 'skip';

// Bounded flag-readiness wait. useFeatureFlag resolves loading→false on resolve
// OR after this timeout (PostHogManager.getFeatureFlag waits then returns the
// vendor default), so a flag timeout keeps Iris on and Hive off. Per
// knowledge eng-4170-prequal-race-history (ai.knowledge/platform): post-auth the
// flag is normally already loaded, so this timeout rarely engages.
const PREQUAL_FLAG_READY_TIMEOUT_MS = 4000;

/** Iris owns this questionnaire; Hive stays off when the remote flag is unavailable. */
export const prequalFlagFallback = (vendor: 'iris' | 'ptzoptics'): boolean => vendor === 'iris';

/**
 * Pure gate decision, separated from the hook so the branch logic is unit-tested
 * without React (rule: ai-code-requires-tests).
 *
 * - Non-candidate (existing user / already prompted): instant 'skip', zero added
 *   latency and no flag dependency.
 * - Candidate, flag still loading: 'loading' → caller HOLDS routing.
 * - Candidate, flag resolved: 'show' iff enabled (kill-switch ON), else 'skip'
 *   (flag OFF — the prod kill-switch / Hive-off path).
 */
export function decidePrequalGate(input: {
  candidate: boolean;
  flagLoading: boolean;
  flagEnabled: boolean;
}): PrequalGateStatus {
  if (!input.candidate) return 'skip';
  if (input.flagLoading) return 'loading';
  return input.flagEnabled ? 'show' : 'skip';
}

/**
 * React hook form, consumed by use-root-bootstrap. Reads the studio user id (the
 * backstop is per-user) and the PREQUAL_WIZARD flag, then defers to
 * decidePrequalGate. The flag must RESOLVE before we render the gate (do not
 * coerce undefined→false): we treat `loading` distinctly and only read `enabled`
 * once loading is false.
 */
export function usePrequalGate(): PrequalGateStatus {
  const { user: auth0User } = useAuth0();
  const { vendor } = useConfig();
  const userId = useDepot((µ) => µ.user?.id);
  const { enabled, loading } = useFeatureFlag(PostHogFeatureFlag.PREQUAL_WIZARD, {
    // A telemetry timeout must not randomly remove required Iris onboarding.
    // The resolved remote flag still acts as the kill switch.
    defaultValue: prequalFlagFallback(vendor),
    timeout: PREQUAL_FLAG_READY_TIMEOUT_MS
  });

  // The questionnaire belongs to web signup; desktop first-login accounts continue to Studio.
  // Auth0 owns account freshness. Route intent does not prove a new account.
  const candidate =
    !isElectronApp() &&
    userId !== undefined &&
    auth0User?.[FIRST_LOGIN_CLAIM] === true &&
    !hasPrequalPrompted(userId);

  return decidePrequalGate({
    candidate,
    flagLoading: loading,
    flagEnabled: enabled === true
  });
}
